import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpNewsletterStatusEnum,
  useArchiveCmpNewsletterRecipientGroupMutation,
  useCancelCmpNewsletterScheduleMutation,
  useCreateCmpNewsletterMutation,
  useGetCmpManageMembersQuery,
  useGetCmpNewsletterDetailQuery,
  useGetCmpNewsletterRecipientGroupsQuery,
  usePreviewCmpNewsletterTestSendMutation,
  usePreviewCmpNewsletterRecipientsLazyQuery,
  usePublishCmpNewsletterToNewsroomMutation,
  useScheduleCmpNewsletterMutation,
  useSendCmpNewsletterNowMutation,
  useSendCmpNewsletterTestMutation,
  useUnpublishCmpNewsletterFromNewsroomMutation,
  useUpdateCmpNewsletterMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import {
  buildEmailDuplicateDraft,
  emailSchedulePartsToUtcMilli,
  emailFormStateToInput,
  newsletterReturnToFormState,
  previewQueryToSummary,
  recipientGroupReturnToUiGroup,
} from "../emailBackendMappers";
import EmailForm from "../EmailForm";
import {
  EmailFormState,
  EmailAudienceMemberOption,
  buildDefaultCreateEmailForm,
} from "../emailMockData";

const UpdateEmail = () => {
  const navigate = useNavigate();
  const { emailId } = useParams<{ emailId: string }>();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";

  const {
    data: detailData,
    loading: detailLoading,
    refetch: refetchEmailDetail,
  } = useGetCmpNewsletterDetailQuery({
    variables: { input: { chamberId, newsletterId: emailId || "" } },
    skip: !chamberId || !emailId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: groupsData,
    refetch: refetchGroups,
  } = useGetCmpNewsletterRecipientGroupsQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: membersData,
  } = useGetCmpManageMembersQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [updateCmpNewsletter, { loading: updatingNewsletter }] =
    useUpdateCmpNewsletterMutation();
  const [createCmpNewsletter] = useCreateCmpNewsletterMutation();
  const [archiveCmpNewsletterRecipientGroup] =
    useArchiveCmpNewsletterRecipientGroupMutation();
  const [previewCmpNewsletterRecipients] =
    usePreviewCmpNewsletterRecipientsLazyQuery({
      fetchPolicy: "network-only",
    });
  const [publishCmpNewsletterToNewsroom] =
    usePublishCmpNewsletterToNewsroomMutation();
  const [unpublishCmpNewsletterFromNewsroom] =
    useUnpublishCmpNewsletterFromNewsroomMutation();
  const [previewCmpNewsletterTestSend, { loading: previewingTestSend }] =
    usePreviewCmpNewsletterTestSendMutation();
  const [sendCmpNewsletterTest, { loading: sendingTestEmail }] =
    useSendCmpNewsletterTestMutation();
  const [sendCmpNewsletterNow, { loading: sendingNow }] =
    useSendCmpNewsletterNowMutation();
  const [scheduleCmpNewsletter, { loading: schedulingNewsletter }] =
    useScheduleCmpNewsletterMutation();
  const [cancelCmpNewsletterSchedule, { loading: cancelingSchedule }] =
    useCancelCmpNewsletterScheduleMutation();

  const backendEmail = detailData?.getCmpNewsletterDetail.newsletter;
  const initialEmail = useMemo(
    () =>
      backendEmail
        ? newsletterReturnToFormState(backendEmail)
        : buildDefaultCreateEmailForm({ chamberName: chamber?.name }),
    [backendEmail, chamber?.name]
  );
  const recipientGroups = useMemo(
    () =>
      (groupsData?.getCmpNewsletterRecipientGroups.groups || []).map(
        recipientGroupReturnToUiGroup
      ),
    [groupsData?.getCmpNewsletterRecipientGroups.groups]
  );
  const memberOptions: EmailAudienceMemberOption[] = useMemo(
    () =>
      (membersData?.getCmpManageMembers.rows || []).map((member) => ({
        id: member.id,
        companyName: member.companyName,
        lifecycleStatus: member.lifecycleStatus,
        membershipTypeId: member.membershipTypeId || "",
        membershipTypeName: member.membershipTypeName || "",
        dateJoinedUtcMilli: member.dateJoinedUtcMilli,
        fullTimeEmployees: member.fullTimeEmployees,
        partTimeEmployees: member.partTimeEmployees,
        renewalDateUtcMilli: member.renewalDateUtcMilli,
        categoryId: member.categoryId,
      })),
    [membersData?.getCmpManageMembers.rows]
  );
  const isInitialLookupLoading =
    !chamberId ||
    (detailLoading && !backendEmail);

  const submitEmail = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await updateCmpNewsletter({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          data: emailFormStateToInput(email),
        },
      },
    });
    const savedEmail = result.data?.updateCmpNewsletter.newsletter;

    if (!savedEmail) {
      throw new Error("The email draft could not be updated.");
    }

    await refetchEmailDetail();

    return newsletterReturnToFormState(savedEmail);
  };

  const archiveRecipientGroup = async (groupId: string) => {
    if (!chamberId) {
      throw new Error("Chamber context is still loading. Try again in a moment.");
    }

    await archiveCmpNewsletterRecipientGroup({
      variables: { input: { chamberId, groupId } },
    });
    await refetchGroups();
  };

  const previewRecipients = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new Error("Chamber context is still loading. Try again in a moment.");
    }

    const result = await previewCmpNewsletterRecipients({
      variables: {
        input: {
          chamberId,
          recipientSelection: emailFormStateToInput(email).recipientSelection!,
        },
      },
    });

    return previewQueryToSummary(result.data?.previewCmpNewsletterRecipients);
  };

  const duplicateAsDraft = async (email: EmailFormState) => {
    if (!chamberId) {
      throw new Error("Chamber context is still loading. Try again in a moment.");
    }

    const duplicatedEmail = buildEmailDuplicateDraft(email);
    const result = await createCmpNewsletter({
      variables: {
        input: {
          chamberId,
          data: {
            ...emailFormStateToInput(duplicatedEmail),
            status: CmpNewsletterStatusEnum.Draft,
          },
        },
      },
    });
    const savedEmail = result.data?.createCmpNewsletter.newsletter;

    if (!savedEmail?.id) {
      throw new Error("The email could not be duplicated.");
    }

    navigate(RoutePaths.chamberAdmin_emails_update.replace(":emailId", savedEmail.id));
  };

  const publishNewsroom = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await publishCmpNewsletterToNewsroom({
      variables: { input: { chamberId, newsletterId: emailId } },
    });
    const savedEmail = result.data?.publishCmpNewsletterToNewsroom.newsletter;

    if (!savedEmail) {
      throw new Error("The newsroom record could not be published.");
    }

    await refetchEmailDetail();

    return newsletterReturnToFormState(savedEmail);
  };

  const unpublishNewsroom = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await unpublishCmpNewsletterFromNewsroom({
      variables: { input: { chamberId, newsletterId: emailId } },
    });
    const savedEmail = result.data?.unpublishCmpNewsletterFromNewsroom.newsletter;

    if (!savedEmail) {
      throw new Error("The newsroom record could not be unpublished.");
    }

    await refetchEmailDetail();

    return newsletterReturnToFormState(savedEmail);
  };

  const previewTestSend = async (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string
  ) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await previewCmpNewsletterTestSend({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          recipientEmails,
          clientRequestId,
          note: email.reviewNote || undefined,
        },
      },
    });
    const preview = result.data?.previewCmpNewsletterTestSend;

    if (!preview) {
      throw new Error("The test email preview could not be loaded.");
    }

    return {
      normalizedRecipientEmails: [...preview.normalizedRecipientEmails],
      fromEmail: preview.fromEmail,
      replyToEmail: preview.replyToEmail,
      explicitRecipientsAllowed: preview.explicitRecipientsAllowed,
      blockingReason: preview.blockingReason,
      confirmationToken: preview.confirmationToken,
      confirmationExpiresAtUtcMilli:
        preview.confirmationExpiresAtUtcMilli,
    };
  };

  const sendReview = async (
    email: EmailFormState,
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await sendCmpNewsletterTest({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          recipientEmails,
          clientRequestId,
          confirmationToken,
          note: email.reviewNote || undefined,
        },
      },
    });
    const sendResult = result.data?.sendCmpNewsletterTest;
    const savedEmail = sendResult?.newsletter;

    if (!sendResult?.newsletterSendQueuedSuccessfully || !savedEmail) {
      throw new Error("The test email could not be sent.");
    }

    const actualRecipientEmails = Array.from(
      new Set(
        sendResult.outboundEmails
          .map((outboundEmail) => outboundEmail.toEmail.trim().toLowerCase())
          .filter(Boolean)
      )
    );

    if (actualRecipientEmails.length === 0) {
      throw new Error(
        "The test email result did not include a confirmed recipient."
      );
    }

    // Delivery is already confirmed by the mutation. A follow-up cache refresh
    // must never turn that successful send into a misleading "could not send"
    // error or encourage an unnecessary retry.
    try {
      await refetchEmailDetail();
    } catch {
      // The mutation result below is the authoritative send result.
    }

    return {
      email: newsletterReturnToFormState(savedEmail),
      actualRecipientEmails,
    };
  };

  const sendNow = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await sendCmpNewsletterNow({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          note: email.reviewNote || undefined,
        },
      },
    });
    const savedEmail = result.data?.sendCmpNewsletterNow.newsletter;

    if (!savedEmail) {
      throw new Error("The email could not be sent.");
    }

    await refetchEmailDetail();

    return newsletterReturnToFormState(savedEmail);
  };

  const scheduleEmail = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const scheduledAtUtcMilli = emailSchedulePartsToUtcMilli(
      email.scheduledDate,
      email.scheduledTime
    );

    if (!scheduledAtUtcMilli) {
      throw new Error("Choose a scheduled date and time before scheduling.");
    }

    const result = await scheduleCmpNewsletter({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          scheduledAtUtcMilli,
          note: email.reviewNote || undefined,
        },
      },
    });
    const savedEmail = result.data?.scheduleCmpNewsletter.newsletter;

    if (!savedEmail) {
      throw new Error("The email could not be scheduled.");
    }

    await refetchEmailDetail();

    return newsletterReturnToFormState(savedEmail);
  };

  const cancelSchedule = async (email: EmailFormState) => {
    if (!chamberId || !emailId) {
      throw new Error("Email context is still loading. Try again in a moment.");
    }

    const result = await cancelCmpNewsletterSchedule({
      variables: {
        input: {
          chamberId,
          newsletterId: emailId,
          note: email.reviewNote || undefined,
        },
      },
    });
    const savedEmail = result.data?.cancelCmpNewsletterSchedule.newsletter;

    if (!savedEmail) {
      throw new Error("The scheduled email could not be canceled.");
    }

    await refetchEmailDetail();

    return newsletterReturnToFormState(savedEmail);
  };

  if (!detailLoading && !backendEmail) {
    return (
      <div className="mx-auto max-w-3xl rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
        <p className="font-bold">Email not found.</p>
        <p className="mt-1">
          Choose an email from Manage Emails to open the backend-connected update
          workflow.
        </p>
        <button
          type="button"
          onClick={() => navigate(RoutePaths.chamberAdmin_emails_manage)}
          className="mt-4 rounded-lg border border-cpaGold bg-cpaGold px-4 py-2 text-sm font-bold text-white transition hover:bg-amber-500"
        >
          Back to Manage Emails
        </button>
      </div>
    );
  }

  return (
    <EmailForm
      mode="update"
      initialEmail={initialEmail}
      recipientGroups={recipientGroups}
      memberOptions={memberOptions}
      isLoading={isInitialLookupLoading}
      isSaving={
        updatingNewsletter ||
        previewingTestSend ||
        sendingTestEmail ||
        sendingNow ||
        schedulingNewsletter ||
        cancelingSchedule
      }
      onSubmit={submitEmail}
      onArchiveRecipientGroup={archiveRecipientGroup}
      onPreviewRecipients={previewRecipients}
      onDuplicateAsDraft={duplicateAsDraft}
      onPublishNewsroom={publishNewsroom}
      onUnpublishNewsroom={unpublishNewsroom}
      onPreviewTestSend={previewTestSend}
      onSendReview={sendReview}
      onSendNow={sendNow}
      onSchedule={scheduleEmail}
      onCancelSchedule={cancelSchedule}
    />
  );
};

export default UpdateEmail;
